<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

import ProjectDetailsStep, { type ProjectDetailsData } from './create-project/ProjectDetailsStep.vue';
import ContactDetailsStep, { type ContactDetailsData } from './create-project/ContactDetailsStep.vue';
import SubmissionStep from './create-project/SubmissionStep.vue';

import { useCreateOrUpdateDraftLead } from '@/app/composables/leads/useCreateOrUpdateDraftLead';
import { useDraftLeadPrefill, type DraftLeadPublicResponse } from '@/app/composables/leads/useDraftLeadPrefill';
import { useDraftLeadAuthCheck } from '@/app/composables/leads/useDraftLeadAuthCheck';
import { useSubmitDraftLead } from '@/app/composables/leads/useSubmitDraftLead';
import { useAuthCheck } from '@/app/composables/leads/useAuthCheck';
import { useExpertMinimal } from '@/app/composables/leads/useExpertMinimal';
import { useMachine } from '@xstate/vue';
import { createProjectMachine } from '@/app/composables/leads/useCreateProjectStateMachine';
import { usePostHog } from '@/app/composables/usePostHog';
import type { LoginAuthResult } from './leads/LoginForm.vue';

type SubmissionMode = 'loading' | 'login' | 'success' | 'error';

// Initialize PostHog tracking
const {
  trackLeadFormStarted,
  trackLeadFormStepViewed,
  trackLeadFormStepCompleted,
  trackLeadFormBackNavigation,
  trackLeadFormSubmitted,
  trackLeadFormSubmitSuccess,
  trackLeadFormSubmitError,
  trackLeadFormAbandoned,
} = usePostHog();

interface Props {
  expertSlug?: string;
  packagedServiceSlug?: string;
  source?: string;
  draftToken?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'success'): void;
}>();

// ==================
// Auth & User State
// ==================

const { isAuthenticated, isClient, user, isLoading: isLoadingAuthUser } = useAuthCheck();
const isAuthenticatedClient = computed(() => isAuthenticated.value && isClient.value);

// ==================
// State Machine
// ==================

const { snapshot, send } = useMachine(createProjectMachine);

const currentState = computed(() => snapshot.value.value as 'projectDetails' | 'contactDetails' | 'submission' | 'success');
const totalSteps = 2; // Only 2 visible steps in the counter (submission step is not counted)

const submissionSuccess = ref(false);
const isNewAccount = ref(false);
const isSubmittingLead = ref(false);
const submissionError = ref(false);
const isPreparingSubmission = ref(false);
const submissionMode = ref<SubmissionMode>('loading');

// ==================
// Draft Lead State
// ==================

const draftToken = ref<string | undefined>(undefined);
const formEmail = ref<string>('');
let createDraftPromise: Promise<string | undefined> | null = null;

// ==================
// Form Data
// ==================

const projectDetailsData = ref<ProjectDetailsData>({
  email: '',
  projectTitle: '',
  projectDescription: '',
  urgency: undefined,
  projectValueRangeId: undefined,
});

const contactDetailsData = ref<ContactDetailsData>({
  storeUrl: '',
  noStoreYet: false,
  brandName: '',
  firstName: '',
  lastName: '',
});

// Track if form has unsaved changes
const hasUnsavedChanges = computed(() => {
  if (submissionSuccess.value) return false;
  
  // Check if any form data has been entered
  const hasProjectDetails = !!projectDetailsData.value.projectTitle || 
                           !!projectDetailsData.value.projectDescription ||
                           !!projectDetailsData.value.email ||
                           projectDetailsData.value.projectValueRangeId !== undefined;
  const hasContactDetails = !!contactDetailsData.value.storeUrl ||
                           !!contactDetailsData.value.firstName ||
                           !!contactDetailsData.value.lastName;
  
  return hasProjectDetails || hasContactDetails;
});

// ==================
// Queries
// ==================

const { expert, packagedService, isLoading: isLoadingExpert } = useExpertMinimal({
  expertSlug: () => props.expertSlug,
  packagedServiceSlug: () => props.packagedServiceSlug,
});

// Check if auth is needed for the draft - enable as soon as we have a draft token
const isOnSubmissionStep = computed(() => currentState.value === 'submission' || currentState.value === 'success');
const { needsAuth, isLoading: isLoadingAuthCheck, data: authCheckData, refetch: refetchAuthCheck } = useDraftLeadAuthCheck({
  token: draftToken,
  enabled: computed(() => !!draftToken.value && !submissionSuccess.value),
  showToast: false, // Don't show toast, we handle errors ourselves
});

// Auth check has data when it has completed at least once
const hasAuthCheckData = computed(() => !!authCheckData.value);

// Determine if user is already authenticated with matching email
const isAuthenticatedWithMatchingEmail = computed(() => {
  return isAuthenticatedClient.value && user.value?.email?.toLowerCase() === formEmail.value.toLowerCase();
});

// Compute the submit button label for step 2 based on what the next step will be
const step2SubmitLabel = computed(() => {
  // If authenticated with matching email → just "Submit"
  if (isAuthenticatedWithMatchingEmail.value) {
    return 'Submit';
  }
  
  // If we have auth check data, use it to determine the label
  if (hasAuthCheckData.value) {
    // needsAuth true means existing account → "Login & Submit"
    // needsAuth false means new account → "Create Account & Submit"
    return needsAuth.value ? 'Login & Submit' : 'Create Account & Submit';
  }
  
  // Default while loading auth check
  return 'Submit';
});

// ==================
// Mutations
// ==================

const { createDraft, updateDraft, isSubmitting: isDraftSubmitting } = useCreateOrUpdateDraftLead();
const { submitDraft, isSubmitting: isLeadSubmitting } = useSubmitDraftLead({ showToast: false });

const isSubmitting = computed(() => isDraftSubmitting.value || isLeadSubmitting.value || isSubmittingLead.value);

function getBrowserTimezone(): string | undefined {
  if (typeof Intl === 'undefined') {
    return undefined;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone || undefined;
}

const browserTimezone = getBrowserTimezone();

async function createDraftWithBrowserTimezone(email: string) {
  const result = await createDraft({ email });
  draftToken.value = result.token;

  if (browserTimezone) {
    try {
      await updateDraft({
        token: result.token,
        timezone: browserTimezone,
      });
    } catch (error) {
      console.error('[CreateProjectForm] Failed to persist browser timezone on draft create:', error);
    }
  }

  return result;
}

function createDraftOnce(email: string): Promise<string | undefined> {
  if (draftToken.value) {
    return Promise.resolve(draftToken.value);
  }

  if (!createDraftPromise) {
    createDraftPromise = (async () => {
      try {
        const result = await createDraftWithBrowserTimezone(email);
        return result.token;
      } catch (error) {
        console.error('[CreateProjectForm] Failed to create draft token:', error);
        return undefined;
      } finally {
        createDraftPromise = null;
      }
    })();
  }

  return createDraftPromise;
}

async function ensureDraftToken(email = formEmail.value): Promise<string | undefined> {
  if (draftPrefillPromise) {
    await draftPrefillPromise;
  }

  if (draftToken.value) {
    return draftToken.value;
  }

  if (!email) {
    return undefined;
  }

  return createDraftOnce(email);
}

async function syncDraftInBackground(): Promise<string | undefined> {
  const token = await ensureDraftToken();
  if (!token) {
    return undefined;
  }

  try {
    await updateDraftWithAllData();
  } catch (error) {
    console.error('[CreateProjectForm] Failed to sync draft data:', error);
  }

  return token;
}

const { fetchDraftForPrefill } = useDraftLeadPrefill();

const isDraftPrefillPending = ref(!!props.draftToken);

const isUnmounted = ref(false);

function waitForSettled(isSettled: () => boolean, timeoutMs = 5000): Promise<void> {
  return new Promise((resolve) => {
    if (isSettled() || isUnmounted.value) {
      resolve();
      return;
    }

    let stopWatching: (() => void) | undefined;
    const timer = window.setTimeout(() => {
      stopWatching?.();
      resolve();
    }, timeoutMs);

    stopWatching = watch(isSettled, (settled) => {
      if (settled) {
        window.clearTimeout(timer);
        stopWatching?.();
        resolve();
      }
    });
  });
}

function applyDraftPrefill(draft: DraftLeadPublicResponse) {
  if (!draftToken.value) {
    draftToken.value = draft.token;
  }

  if (draft.email && !projectDetailsData.value.email) {
    projectDetailsData.value.email = draft.email;
  }
  if (draft.email && !formEmail.value) {
    formEmail.value = draft.email;
  }
  if (draft.project_title && !projectDetailsData.value.projectTitle) {
    projectDetailsData.value.projectTitle = draft.project_title;
  }
  if (draft.project_description && !projectDetailsData.value.projectDescription) {
    projectDetailsData.value.projectDescription = draft.project_description;
  }
  if (draft.urgency && !projectDetailsData.value.urgency) {
    projectDetailsData.value.urgency = draft.urgency;
  }
  if (draft.first_name && !contactDetailsData.value.firstName) {
    contactDetailsData.value.firstName = draft.first_name;
  }
  if (draft.last_name && !contactDetailsData.value.lastName) {
    contactDetailsData.value.lastName = draft.last_name;
  }
  if (draft.store_url && !contactDetailsData.value.storeUrl) {
    contactDetailsData.value.storeUrl = draft.store_url;
  }
  if (draft.brand_name && !contactDetailsData.value.brandName) {
    contactDetailsData.value.brandName = draft.brand_name;
  }
}

async function runDraftPrefill(token: string): Promise<void> {
  let hydrated = false;

  try {
    const draft = await fetchDraftForPrefill(token);

    if (draft) {
      await waitForSettled(() => !isLoadingAuthUser.value);
      await waitForSettled(() => !isLoadingExpert.value);

      if (isUnmounted.value) {
        return;
      }

      applyDraftPrefill(draft);
      hydrated = true;
    }
  } catch (error) {
    console.warn('[CreateProjectForm] Draft prefill failed, continuing with an empty form:', error);
  } finally {
    isDraftPrefillPending.value = false;

    if (!hydrated && !isUnmounted.value && !draftToken.value && isAuthenticatedClient.value && formEmail.value) {
      createDraftOnce(formEmail.value).catch((error) => {
        console.error('[CreateProjectForm] Failed to create draft after prefill fallback:', error);
      });
    }
  }
}

const draftPrefillPromise: Promise<void> | null = props.draftToken
  ? runDraftPrefill(props.draftToken)
  : null;

// ==================
// Pre-fill email if authenticated and create draft
// ==================

watch([isAuthenticatedClient, user], async ([isAuth, userData]) => {
  if (isAuth && userData?.email && !projectDetailsData.value.email) {
    projectDetailsData.value.email = userData.email;
    formEmail.value = userData.email;
    
    if (!draftToken.value && !isDraftPrefillPending.value) {
      try {
        await createDraftOnce(userData.email);
      } catch (error) {
        console.error('[CreateProjectForm] Failed to create draft for pre-filled email:', error);
      }
    }
  }
}, { immediate: true });

// Pre-fill project title and description with packaged service name when it loads
watch([packagedService, expert], ([newService, expertData]) => {
  if (newService?.name) {
    if (!projectDetailsData.value.projectTitle) {
      projectDetailsData.value.projectTitle = newService.name;
    }
    if (!projectDetailsData.value.projectDescription && expertData?.name) {
      const expertFirstName = expertData.name.split(' ')[0];
      projectDetailsData.value.projectDescription = `Hey ${expertFirstName},\n\nI'm interested in proceeding with the ${newService.name} packaged service.`;
    } else if (!projectDetailsData.value.projectDescription) {
      projectDetailsData.value.projectDescription = `I'm interested in proceeding with the ${newService.name} packaged service.`;
    }
  }
}, { immediate: true });

// ==================
// Step Handlers
// ==================

function handleEmailBlur(email: string) {
  // Only create draft if we don't already have a token
  if (draftToken.value) {
    return;
  }
  
  formEmail.value = email;

  void ensureDraftToken(email);
}

async function handleProjectDetailsFieldBlur(data: Partial<ProjectDetailsData>) {
  // Update local state
  Object.assign(projectDetailsData.value, data);
  
  // Update draft if we have a token
  if (draftToken.value) {
    try {
      await updateDraftWithAllData();
    } catch (error) {
      console.error('[CreateProjectForm] Failed to update draft on step 1 blur:', error);
    }
  }
}

function handleProjectDetailsNext(data: ProjectDetailsData) {
  projectDetailsData.value = data;
  formEmail.value = data.email;

  void syncDraftInBackground();

  // Track step completion
  trackLeadFormStepCompleted('projectDetails', 1, {
    has_project_description: !!data.projectDescription,
    urgency: data.urgency,
  });

  send({ type: 'NEXT' });

  // Track next step view
  trackLeadFormStepViewed('contactDetails', 2, totalSteps);
}

async function handleContactDetailsNext(data: ContactDetailsData) {
  contactDetailsData.value = data;

  submissionError.value = false;
  submissionSuccess.value = false;
  submissionMode.value = 'loading';

  // Track step completion
  trackLeadFormStepCompleted('contactDetails', 2, {
    has_brand_name: !!data.brandName,
  });
  
  send({ type: 'NEXT' });

  // Track submission step view
  trackLeadFormStepViewed('submission', 3, totalSteps);

  await orchestrateSubmission();
}

function handleContactDetailsBack() {
  trackLeadFormBackNavigation('contactDetails', 'projectDetails');
  send({ type: 'BACK' });
  trackLeadFormStepViewed('projectDetails', 1, totalSteps);
}

async function handleContactDetailsFieldBlur(data: Partial<ContactDetailsData>) {
  // Update local state
  Object.assign(contactDetailsData.value, data);
  
  // Update draft with current field values
  if (draftToken.value) {
    try {
      await updateDraftWithAllData();
    } catch (error) {
      console.error('[CreateProjectForm] Failed to update draft on blur:', error);
    }
  } else {
    console.warn('[CreateProjectForm] No draft token available for step 2 blur update');
  }
}

function handleSubmissionBack() {
  trackLeadFormBackNavigation('submission', 'contactDetails');
  send({ type: 'BACK' });
  trackLeadFormStepViewed('contactDetails', 2, totalSteps);
}

async function handleAuthenticated(_data: LoginAuthResult) {
  // Track auth step completion
  trackLeadFormStepCompleted('submission', 3);
  
  // Submit the draft after authentication
  await handleSubmit();
}

async function handleSubmit(options: { syncBeforeSubmit?: boolean } = {}) {
  // Prevent double submission
  if (isSubmittingLead.value) {
    return;
  }

  const { syncBeforeSubmit = true } = options;
  
  isSubmittingLead.value = true;
  submissionError.value = false;
  submissionMode.value = 'loading';
  
  try {
    const token = syncBeforeSubmit ? await syncDraftInBackground() : draftToken.value;
    if (!token) {
      submissionError.value = true;
      submissionMode.value = 'error';
      return;
    }

    // Submit the draft
    const success = await submitLeadDraft();
    if (!success) {
      submissionError.value = true;
      submissionMode.value = 'error';
    } else {
      submissionMode.value = 'success';
    }
  } catch (error) {
    console.error('[CreateProjectForm] handleSubmit error:', error);
    submissionError.value = true;
    submissionMode.value = 'error';
  } finally {
    isSubmittingLead.value = false;
  }
}

async function orchestrateSubmission() {
  if (isSubmittingLead.value) {
    return;
  }

  isPreparingSubmission.value = true;
  submissionError.value = false;
  submissionMode.value = 'loading';

  try {
    const token = await syncDraftInBackground();
    if (!token) {
      submissionError.value = true;
      submissionMode.value = 'error';
      return;
    }

    if (!isAuthenticatedWithMatchingEmail.value) {
      const authCheckResult = await refetchAuthCheck();
      const authRequired = authCheckResult.data?.needs_auth ?? needsAuth.value;

      if (authRequired) {
        submissionMode.value = 'login';
        return;
      }
    }

    await handleSubmit({ syncBeforeSubmit: false });
  } catch (error) {
    console.error('[CreateProjectForm] orchestrateSubmission error:', error);
    submissionError.value = true;
    submissionMode.value = 'error';
  } finally {
    isPreparingSubmission.value = false;
  }
}

// ==================
// Helper Functions
// ==================

async function updateDraftWithAllData() {
  if (!draftToken.value) return;
  
  const hasPreferredExpert = !!props.expertSlug && !!expert.value;
  const matchingStrategy = hasPreferredExpert ? 'selected' : 'auto';
  
  await updateDraft({
    token: draftToken.value,
    projectTitle: projectDetailsData.value.projectTitle,
    projectDescription: projectDetailsData.value.projectDescription || undefined,
    urgency: projectDetailsData.value.urgency,
    projectValueRangeId: projectDetailsData.value.projectValueRangeId,
    storeUrl: contactDetailsData.value.storeUrl || undefined,
    brandName: contactDetailsData.value.brandName || undefined,
    firstName: contactDetailsData.value.firstName || undefined,
    lastName: contactDetailsData.value.lastName || undefined,
    selectedExpertId: expert.value?.id,
    selectedPackagedServiceId: packagedService.value?.id,
    matchingStrategy,
    timezone: browserTimezone,
  });
}

async function submitLeadDraft(): Promise<boolean> {
  if (!draftToken.value) {
    console.error('[CreateProjectForm] No draft token available');
    return false;
  }
  
  try {
    const hasPreferredExpert = !!props.expertSlug && !!expert.value;
    
    // Track form submission attempt
    trackLeadFormSubmitted({
      hasPreferredExpert,
      hasPackagedService: !!packagedService.value?.id,
      urgency: projectDetailsData.value.urgency,
      storeMode: contactDetailsData.value.noStoreYet ? 'new' : 'existing',
      matchingStrategy: hasPreferredExpert ? 'selected' : 'auto',
    });
    
    const result = await submitDraft(draftToken.value);
    
    // Track submission success
    trackLeadFormSubmitSuccess();
    
    isNewAccount.value = result.isNewAccount;
    submissionSuccess.value = true;
    emit('success');
    send({ type: 'SUBMIT' });
    return true;
    
  } catch (error: any) {
    console.error('[CreateProjectForm] Submit error:', error);
    
    // Track submission error
    trackLeadFormSubmitError(error?.message || 'Unknown error');
    
    return false;
  }
}

// ==================
// Unsaved Changes Warning
// ==================

function handleBeforeUnload(event: BeforeUnloadEvent) {
  if (hasUnsavedChanges.value) {
    event.preventDefault();
    event.returnValue = '';
    return '';
  }
}

// Router navigation guard (component-level)
onBeforeRouteLeave((_to, _from, next) => {
  if (hasUnsavedChanges.value) {
    const confirmed = window.confirm(
      'You have unsaved changes. Are you sure you want to leave? Your progress will be lost.'
    );
    if (!confirmed) {
      next(false);
      return;
    }
  }
  next();
});

onMounted(() => {
  // Add beforeunload listener for browser navigation (refresh, close tab, etc.)
  window.addEventListener('beforeunload', handleBeforeUnload);

  // Track form started
  trackLeadFormStarted({
    expertSlug: props.expertSlug,
    packagedServiceSlug: props.packagedServiceSlug,
    source: props.source || window.location.pathname,
    referrer: document.referrer || undefined,
  });

  // Track initial step view
  trackLeadFormStepViewed('projectDetails', 1, totalSteps);
});

onBeforeUnmount(() => {
  isUnmounted.value = true;

  // Remove beforeunload listener
  window.removeEventListener('beforeunload', handleBeforeUnload);

  // Track form abandoned if not successfully submitted
  if (!submissionSuccess.value && hasUnsavedChanges.value) {
    trackLeadFormAbandoned(currentState.value);
  }
});

watch(isOnSubmissionStep, (isOnSubmission) => {
  if (!isOnSubmission) {
    isPreparingSubmission.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Step 1: Project Details -->
    <ProjectDetailsStep
      v-show="currentState === 'projectDetails'"
      v-model="projectDetailsData"
      :expert="expert ?? undefined"
      :packaged-service="packagedService ?? undefined"
      :is-loading-expert="Boolean(isLoadingExpert && expertSlug)"
      :has-draft-token="!!draftToken"
      :current-step="1"
      :total-steps="totalSteps"
      @next="handleProjectDetailsNext"
      @email-blur="handleEmailBlur"
      @field-blur="handleProjectDetailsFieldBlur"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </ProjectDetailsStep>

    <!-- Step 2: Contact Details -->
    <ContactDetailsStep
      v-show="currentState === 'contactDetails'"
      v-model="contactDetailsData"
      :current-step="2"
      :total-steps="totalSteps"
      :user-first-name="user?.first_name"
      :user-last-name="user?.last_name"
      :submit-label="step2SubmitLabel"
      @next="handleContactDetailsNext"
      @back="handleContactDetailsBack"
      @field-blur="handleContactDetailsFieldBlur"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </ContactDetailsStep>

    <!-- Step 3: Submission (auth or thank you) -->
    <SubmissionStep
      v-show="isOnSubmissionStep"
      :current-step="3"
      :total-steps="totalSteps"
      :mode="submissionMode"
      :is-loading-auth-check="isLoadingAuthCheck"
      :has-draft-token="!!draftToken"
      :has-auth-check-data="hasAuthCheckData"
      :is-active="isOnSubmissionStep"
      :is-authenticated="isAuthenticatedClient"
      :is-submitting="isSubmitting"
      :is-submitted="submissionSuccess"
      :is-new-account="isNewAccount"
      :has-error="submissionError"
      :is-preparing-submission="isPreparingSubmission"
      @authenticated="handleAuthenticated"
      @back="handleSubmissionBack"
      @retry="orchestrateSubmission"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </SubmissionStep>
  </div>
</template>
